// Utilitaire pour charger et parser les CSV côté client (pour Tauri)

// Fonction pour parser une ligne CSV avec gestion des guillemets
function parseCSVLine(line: string): string[] {
  const result: string[] = []
  let current = ''
  let inQuotes = false
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    
    if (char === '"') {
      inQuotes = !inQuotes
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim())
      current = ''
    } else {
      current += char
    }
  }
  
  result.push(current.trim())
  return result
}

// Fonction pour parser une résolution comme "3840 × 2160 px @60 Hz" ou "7680 max largeur / 7680 max hauteur"
function parseResolution(resStr: string): { width: number; height: number } | string {
  if (!resStr || resStr === 'N/A') return 'N/A'
  
  // Si c'est le format "max largeur / max hauteur", retourner tel quel
  if (resStr.includes('max largeur') || resStr.includes('max hauteur')) {
    return resStr
  }
  
  const match = resStr.match(/(\d+)\s*[×x]\s*(\d+)/)
  if (match) {
    return {
      width: parseInt(match[1]),
      height: parseInt(match[2])
    }
  }
  return resStr // Retourner la chaîne telle quelle au lieu de N/A
}

// Fonction pour extraire un nombre d'une chaîne
function parseNumber(str: string): number | string {
  if (!str || str === 'N/A') return 'N/A'
  
  const match = str.match(/(\d+(?:\s\d+)*)/)
  if (match) {
    return parseInt(match[1].replace(/\s/g, ''))
  }
  return 'N/A'
}

// Charger et parser les processeurs depuis un CSV
export async function loadProcessorsFromCSV(): Promise<any[]> {
  try {
    const response = await fetch('/data/led_processor_specs.csv')
    if (!response.ok) {
      throw new Error(`Failed to load processors CSV: ${response.statusText}`)
    }
    
    const fileContent = await response.text()
    const lines = fileContent.split('\n').filter(line => line.trim())
    
    if (lines.length === 0) {
      return []
    }
    
    const processors = lines.slice(1).map((line, index) => {
      const values = parseCSVLine(line)
      const brand = values[0]?.trim() || 'N/A'
      const model = values[1]?.trim() || 'N/A'
      
      const imageName = values[12]?.trim() || '' // Colonne Image (colonne 13)
      
      return {
        id: `proc-${index + 1}`,
        brand,
        model,
        name: `${model} ${brand}`,
        maxInputResolution: parseResolution(values[2]?.trim()),
        maxOutputResolution: parseResolution(values[3]?.trim()),
        colorDepth: values[4]?.trim() || 'N/A',
        pixelClockMax: 'N/A', // Pas dans le CSV
        maxPixelsPerPort: parseNumber(values[5]?.trim()),
        totalOutputPixels: parseNumber(values[6]?.trim()),
        outputPorts: parseNumber(values[7]?.trim()),
        opticalPorts: values[8]?.trim() || 'N/A', // Ports optiques/fibre (colonne 9)
        videoInputsCount: parseNumber(values[9]?.trim()), // Nb entrées vidéo (colonne 10)
        powerConsumption: parseNumber(values[10]?.trim()), // Consommation (colonne 11)
        videoInputs: values[11]?.trim() || 'N/A', // Type d'entrées vidéo (colonne 12)
        image: imageName ? `/images/processors/${imageName}` : '/images/processors/placeholder.png',
        imageDimensions: { width: 400, height: 200 }
      }
    })
    
    return processors
  } catch (error) {
    console.error('Error loading processors from CSV:', error)
    return []
  }
}

// Charger et parser les panneaux depuis un CSV
export async function loadPanelsFromCSV(): Promise<any[]> {
  try {
    const response = await fetch('/data/led_panels_specs.csv')
    if (!response.ok) {
      throw new Error(`Failed to load panels CSV: ${response.statusText}`)
    }
    
    const fileContent = await response.text()
    const lines = fileContent.split('\n').filter(line => line.trim())
    
    if (lines.length === 0) {
      return []
    }
    
    const panels = lines.slice(1).map((line, index) => {
      const values = parseCSVLine(line)
      
      return {
        id: `panel-${index + 1}`,
        brand: values[0]?.trim() || 'N/A',
        model: values[1]?.trim() || 'N/A',
        name: values[2]?.trim() || 'N/A',
        pixelPitch: parseFloat(values[3]?.trim()) || 0,
        resolution: {
          width: parseInt(values[4]?.trim()) || 0,
          height: parseInt(values[5]?.trim()) || 0
        },
        dimensions: {
          width: parseFloat(values[6]?.trim()) || 0,
          height: parseFloat(values[7]?.trim()) || 0
        },
        brightness: parseInt(values[8]?.trim()) || 0,
        powerConsumption: parseInt(values[9]?.trim()) || 0,
        ipRating: values[10]?.trim() || 'N/A'
      }
    })
    
    return panels
  } catch (error) {
    console.error('Error loading panels from CSV:', error)
    return []
  }
}

// Charger les panneaux depuis le JSON LEDWallCentral scrapé (Tauri)
export async function loadPanelsFromLWC(): Promise<any[]> {
  try {
    const response = await fetch('/data/led_panels_lwc.json')
    if (!response.ok) return []
    const data = await response.json()
    if (!Array.isArray(data)) return []
    return data
  } catch {
    return []
  }
}

// Charger et parser les armoires depuis un CSV
export async function loadCabinetsFromCSV(): Promise<any[]> {
  try {
    const response = await fetch('/data/power_cabinets_specs.csv')
    if (!response.ok) {
      throw new Error(`Failed to load cabinets CSV: ${response.statusText}`)
    }
    
    const fileContent = await response.text()
    const lines = fileContent.split('\n').filter(line => line.trim())
    
    if (lines.length === 0) {
      return []
    }
    
    const cabinets = lines.slice(1).map((line, index) => {
      const values = parseCSVLine(line)
      
      return {
        id: `cabinet-${index + 1}`,
        brand: values[0]?.trim() || 'N/A',
        model: values[1]?.trim() || 'N/A',
        name: values[2]?.trim() || 'N/A',
        type: values[3]?.trim() || 'N/A',
        maxPower: parseInt(values[4]?.trim()) || 0,
        outlets: parseInt(values[5]?.trim()) || 0,
        voltage: values[6]?.trim() || 'N/A',
        dimensions: {
          width: parseFloat(values[7]?.trim()) || 0,
          height: parseFloat(values[8]?.trim()) || 0
        }
      }
    })
    
    return cabinets
  } catch (error) {
    console.error('Error loading cabinets from CSV:', error)
    return []
  }
}
